import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  previewRhExcel,
  generateRhCampaign,
  previewRhInvoicePdf,
} from '../../api/index'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import { useRhGuard } from '../../hooks/useRoleGuard'
import styles from './ImportMensuel.module.css'

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
]

const formatTND = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const isExcelFile = (f) => {
  const name = f?.name?.toLowerCase() || ''
  return name.endsWith('.xlsx') || name.endsWith('.xls')
}

const ImportMensuel = () => {
  const history = useHistory()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  useRhGuard()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('profile'))
    } catch {
      return null
    }
  }, [])

  const uploadedBy = user?.result?._id || 'rh'

  const now = new Date()
  const params = new URLSearchParams(location.search)
  const initialMonth = Number(params.get('month')) || now.getMonth() + 1
  const initialYear = Number(params.get('year')) || now.getFullYear()
  const [month, setMonth] = useState(initialMonth)
  const [year, setYear] = useState(initialYear)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [showErrorsOnly, setShowErrorsOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [previewPdfRow, setPreviewPdfRow] = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)
  const dragCounter = useRef(0)
  const lastAnalyzedKeyRef = useRef('')
  const analyzeRequestIdRef = useRef(0)

  const periodLabel = `${MONTHS.find((m) => m.value === month)?.label} ${year}`

  const previewStats = useMemo(() => {
    if (!preview?.rows) return null
    const validRows = preview.rows.filter((r) => r.valid)
    const totalHonoraires = validRows.reduce((acc, r) => acc + (r.total || 0), 0)
    const totalNet = validRows.reduce((acc, r) => acc + (r.netAPayer || r.total + 1 || 0), 0)
    return { totalHonoraires, totalNet }
  }, [preview])

  const validPreviewRows = useMemo(
    () => (preview?.rows || []).filter((r) => r.valid),
    [preview]
  )

  const getValidRowSequence = (row) =>
    validPreviewRows.findIndex((r) => r.rowIndex === row.rowIndex) + 1

  const filteredRows = useMemo(() => {
    if (!preview?.rows) return []
    const q = search.trim().toLowerCase()
    return preview.rows.filter((row) => {
      if (showErrorsOnly && row.valid) return false
      if (!q) return true
      const haystack = [row.name, row.email, row.matriculeFiscal].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [preview, showErrorsOnly, search])

  useEffect(() => {
    if (!user) history.push('/')
  }, [user, history])

  useEffect(() => {
    if (!user || !file) {
      if (!file) {
        setPreview(null)
        lastAnalyzedKeyRef.current = ''
      }
      return undefined
    }

    const analyzeKey = `${file.name}|${file.size}|${file.lastModified}|${month}|${year}`
    if (lastAnalyzedKeyRef.current === analyzeKey) {
      setLoading(false)
      return undefined
    }

    let cancelled = false
    const requestId = ++analyzeRequestIdRef.current
    setLoading(true)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)
    formData.append('year', year)
    formData.append('uploadedBy', uploadedBy)

    previewRhExcel(formData)
      .then(({ data }) => {
        if (!cancelled) {
          setPreview(data)
          lastAnalyzedKeyRef.current = analyzeKey
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setPreview(null)
          lastAnalyzedKeyRef.current = ''
          openSnackbarRef.current(
            error?.response?.data?.message || 'Erreur lors de la lecture du fichier'
          )
        }
      })
      .finally(() => {
        if (!cancelled && requestId === analyzeRequestIdRef.current) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [file, month, year, uploadedBy, user])

  if (!user) return null

  const applyFile = (selectedFile) => {
    if (!selectedFile) return
    if (!isExcelFile(selectedFile)) {
      openSnackbar('Format invalide — utilisez un fichier .xlsx ou .xls')
      return
    }
    setFile(selectedFile)
  }

  const handleFileChange = (e) => {
    applyFile(e.target.files[0] || null)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current += 1
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    applyFile(e.dataTransfer.files?.[0])
  }

  const buildFormData = () => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)
    formData.append('year', year)
    formData.append('uploadedBy', uploadedBy)
    return formData
  }

  const fetchPreview = async (showToast = true) => {
    const { data } = await previewRhExcel(buildFormData())
    setPreview(data)
    if (showToast) {
      openSnackbar(`${data.validCount} lignes valides sur ${data.totalRows}`)
    }
    return data
  }

  const handlePreview = async () => {
    if (!file) {
      openSnackbar('Veuillez sélectionner un fichier Excel')
      return
    }

    lastAnalyzedKeyRef.current = ''
    setLoading(true)
    try {
      const data = await fetchPreview()
      if (data) {
        lastAnalyzedKeyRef.current = `${file.name}|${file.size}|${file.lastModified}|${month}|${year}`
      }
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur lors de la lecture du fichier')
    } finally {
      setLoading(false)
    }
  }

  const executeGenerate = async () => {
    setGenerating(true)
    try {
      const { data } = await generateRhCampaign(buildFormData())
      openSnackbar(data.message)
      setConfirmModal({
        type: 'sendEmails',
        variant: 'success',
        title: 'Factures générées',
        message: 'La campagne a été créée avec succès.',
        detail: 'Souhaitez-vous envoyer tous les emails aux coaches maintenant ?',
        confirmLabel: 'Envoyer les emails',
        cancelLabel: 'Plus tard',
        campaignId: data.campaign._id,
      })
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur lors de la génération')
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerate = async () => {
    if (!file) {
      openSnackbar('Veuillez sélectionner un fichier Excel')
      return
    }

    if (loading) {
      openSnackbar('Analyse du fichier en cours — patientez quelques secondes')
      return
    }

    let currentPreview = preview
    const previewStale = !currentPreview
      || currentPreview.month !== month
      || currentPreview.year !== year
      || currentPreview.fileName !== file.name

    if (previewStale) {
      setLoading(true)
      try {
        currentPreview = await fetchPreview(false)
      } catch (error) {
        openSnackbar(error?.response?.data?.message || 'Erreur lors de la lecture du fichier')
        return
      } finally {
        setLoading(false)
      }
    }

    if (!currentPreview) {
      openSnackbar('Analyse du fichier requise — vérifiez le format Excel')
      return
    }

    if (currentPreview?.hasDuplicates) {
      openSnackbar('Doublons MF ou email détectés — corrigez le fichier avant de générer')
      return
    }

    if (currentPreview.errorCount > 0) {
      setConfirmModal({
        type: 'errors',
        variant: 'warning',
        title: 'Lignes en erreur',
        message: `${currentPreview.errorCount} ligne(s) en erreur seront ignorées lors de la génération.`,
        detail: 'Seules les lignes valides produiront une facture. Vous pourrez corriger le fichier et relancer un import ultérieurement.',
        confirmLabel: 'Continuer',
        cancelLabel: 'Annuler',
      })
      return
    }

    executeGenerate()
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return

    if (confirmModal.type === 'errors') {
      setConfirmModal(null)
      executeGenerate()
      return
    }

    if (confirmModal.type === 'sendEmails') {
      const { campaignId } = confirmModal
      setConfirmModal(null)
      history.push(`/campagnes/${campaignId}?sendAll=1`)
    }
  }

  const handleCancelModal = () => {
    if (!confirmModal) return

    if (confirmModal.type === 'sendEmails') {
      const { campaignId } = confirmModal
      setConfirmModal(null)
      history.push(`/campagnes/${campaignId}`)
      return
    }

    setConfirmModal(null)
  }

  const previewRowPdf = async (row) => {
    if (!row?.valid) {
      openSnackbar('Cette ligne contient des erreurs — aperçu impossible')
      return
    }

    const sequence = getValidRowSequence(row)
    setPreviewPdfRow(row.rowIndex)

    try {
      const response = await previewRhInvoicePdf({ row, month, year, sequence })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    } catch {
      openSnackbar('Erreur génération aperçu PDF')
    } finally {
      setPreviewPdfRow(null)
    }
  }

  const currentStep = !file ? 1 : !preview ? 2 : 3

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => history.push('/dashboard')}>
        ← Retour au tableau de bord
      </button>

      <div className={styles.topBar}>
        <div>
          <h1>Import mensuel</h1>
          <p>California Gym — Génération des factures coaches</p>
        </div>
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>Période de facturation</span>
          <strong>{periodLabel}</strong>
        </div>
        <div className={styles.periodStatus}>
          <span className={styles.badgeInfo}>Client : Société Sport et Loisirs</span>
          <span>Droit de timbre : 1 TND / facture</span>
        </div>
      </div>

      <div className={styles.steps}>
        <div className={`${styles.step} ${currentStep >= 1 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>1</span>
          <span>Paramètres & fichier</span>
        </div>
        <div className={styles.stepLine} />
        <div className={`${styles.step} ${currentStep >= 2 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>2</span>
          <span>Analyse & validation</span>
        </div>
        <div className={styles.stepLine} />
        <div className={`${styles.step} ${currentStep >= 3 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>3</span>
          <span>Génération</span>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <h2>Configuration de l'import</h2>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label htmlFor="import-month">Mois</label>
              <select
                id="import-month"
                className={styles.select}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label htmlFor="import-year">Année</label>
              <select
                id="import-year"
                className={styles.select}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div
            className={styles.uploadZone}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <label
              htmlFor="import-file"
              className={`${styles.uploadLabel} ${isDragging ? styles.uploadDragActive : ''} ${file ? styles.uploadHasFile : ''}`}
            >
              <span className={styles.uploadIcon}>{isDragging ? '📥' : '📄'}</span>
              <strong>
                {isDragging
                  ? 'Déposez le fichier ici'
                  : file
                    ? file.name
                    : 'Sélectionner le fichier Excel RH'}
              </strong>
              <span className={styles.uploadHint}>
                Glissez-déposez ou cliquez — Format .xlsx — analyse automatique à l&apos;import
              </span>
              <span className={styles.uploadBtn}>Parcourir</span>
            </label>
            <input
              id="import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className={styles.fileInput}
            />
          </div>

          <div className={styles.actions}>
            <button
              className={styles.btnSecondary}
              onClick={handlePreview}
              disabled={loading || !file}
            >
              {loading ? 'Analyse en cours...' : 'Réanalyser le fichier'}
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleGenerate}
              disabled={
                generating
                || loading
                || !file
                || !preview
                || preview?.hasDuplicates
                || preview?.validCount === 0
              }
              title={
                loading
                  ? 'Analyse du fichier en cours…'
                  : !preview && file
                    ? 'Attendez la fin de l\'analyse automatique'
                    : preview?.hasDuplicates
                      ? 'Corrigez les doublons MF/email dans le fichier Excel'
                      : undefined
              }
            >
              {generating ? 'Génération...' : `Générer ${preview?.validCount || ''} facture(s)`}
            </button>
          </div>
        </div>

        <div className={styles.panel}>
          <h2>Format Excel attendu</h2>
          <ul className={styles.formatList}>
            <li><span>A</span> Matricule fiscal (MF)</li>
            <li><span>B</span> Nom & Prénom</li>
            <li><span>C</span> Adresse</li>
            <li><span>D</span> Honoraires fixes <em>(info)</em></li>
            <li><span>E</span> Honoraires variables <em>(info)</em></li>
            <li><span>F</span> <strong>Total</strong> (montant facturé)</li>
            <li><span>G</span> Email du coach</li>
          </ul>
          <div className={styles.noteBox}>
            <strong>Calcul facture :</strong> Net à payer = Total (col. F) + 1 TND (droit de timbre)
          </div>
        </div>
      </div>

      {preview && (
        <>
          <div className={styles.kpiGrid}>
            <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
              <span className={styles.kpiLabel}>Lignes valides</span>
              <span className={styles.kpiValue}>{preview.validCount}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Erreurs</span>
              <span className={`${styles.kpiValue} ${preview.errorCount > 0 ? styles.kpiError : ''}`}>
                {preview.errorCount}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Total honoraires</span>
              <span className={styles.kpiValue}>{formatTND(previewStats?.totalHonoraires)} TND</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Net à payer</span>
              <span className={styles.kpiValue}>{formatTND(previewStats?.totalNet)} TND</span>
            </div>
          </div>

          {preview.hasDuplicates && (
            <div className={styles.alertBox}>
              <strong>Génération bloquée</strong> — MF ou email en double dans le fichier.
              Corrigez le Excel (chaque coach doit avoir un MF et un email uniques) puis réanalysez.
            </div>
          )}

          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>Aperçu des données — {preview.fileName || file?.name}</h2>
              <span className={styles.pageInfo}>{filteredRows.length} ligne(s) affichée(s)</span>
            </div>

            <div className={styles.toolbar}>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Rechercher un coach..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <label className={styles.checkFilter}>
                <input
                  type="checkbox"
                  checked={showErrorsOnly}
                  onChange={(e) => setShowErrorsOnly(e.target.checked)}
                />
                Erreurs uniquement
              </label>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Ligne</th>
                    <th>MF</th>
                    <th>Coach</th>
                    <th>Email</th>
                    <th>Total</th>
                    <th>Net à payer</th>
                    <th>Statut</th>
                    <th>Aperçu</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.rowIndex} className={row.valid ? '' : styles.errorRow}>
                      <td>{row.rowIndex}</td>
                      <td>{row.matriculeFiscal}</td>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{formatTND(row.total)} TND</td>
                      <td>{formatTND(row.netAPayer || row.total + 1)} TND</td>
                      <td>
                        {row.valid ? (
                          <span className={styles.badgeOk}>Valide</span>
                        ) : (
                          <span className={styles.badgeErr} title={row.rowErrors?.join(', ')}>
                            {row.rowErrors?.[0] || 'Erreur'}
                          </span>
                        )}
                      </td>
                      <td>
                        {row.valid ? (
                          <button
                            type="button"
                            className={styles.rowPdfBtn}
                            disabled={previewPdfRow === row.rowIndex}
                            onClick={() => previewRowPdf(row)}
                            title={`Aperçu facture n° ${getValidRowSequence(row)}/${String(month).padStart(2, '0')}/${year} — ${row.name}`}
                          >
                            {previewPdfRow === row.rowIndex ? '…' : 'PDF'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmModal)}
        variant={confirmModal?.variant || 'info'}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Confirmer'}
        cancelLabel={confirmModal?.cancelLabel || 'Annuler'}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}
        loading={generating && confirmModal?.type === 'errors'}
      />
    </div>
  )
}

export default ImportMensuel
